// "Instalar o app": a loja na tela inicial do celular, aberta em tela cheia
// direto no cardápio. O manifest por loja sai do hooks/useShopManifest.js;
// aqui fica como oferecer. O card aparece sempre na tela de status, até a
// loja estar instalada.
//
// - "prompt": Android (Chrome, Samsung Internet, Edge) dispara o
//   `beforeinstallprompt` quando a loja é instalável. Ele é guardado aqui e
//   o botão do card abre o prompt nativo. O evento chega no boot, antes de
//   qualquer tela montar: por isso `listenInstallPrompt` roda no App.js.
// - "ios": não há prompt. Passo a passo do Compartilhar → Adicionar à Tela
//   de Início.
// - "in-app": navegador embutido (Instagram, Facebook, TikTok, WebView) não
//   instala. Instrução para abrir no navegador do celular.
// - "manual": sem prompt (Firefox, loja sem logo, prompt já usado).
//   Instrução pelo menu do navegador.
//
// "Já instalado" só é visível de dentro do app (display-mode standalone) ou
// logo após instalar (`appinstalled`). Aberta no navegador comum, a loja
// instalada continua mostrando o card: o navegador não informa isso.

const IN_APP_BROWSER =
  /FBAN|FBAV|FB_IAB|Instagram|Line\/|musical_ly|BytedanceWebview|Snapchat|; wv\)/i;

export function isInAppBrowser(userAgent) {
  return IN_APP_BROWSER.test(userAgent || "");
}

export function isIOS(userAgent, maxTouchPoints = 0) {
  const ua = userAgent || "";
  // iPad com iPadOS 13+ se apresenta como Mac
  return (
    /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1)
  );
}

export function isStandalone(win = window) {
  try {
    return (
      win.navigator.standalone === true ||
      win.matchMedia("(display-mode: standalone)").matches
    );
  } catch {
    return false;
  }
}

/**
 * Como oferecer a instalação neste aparelho: "prompt", "ios", "in-app",
 * "manual", ou null quando já está instalado.
 */
export function installMethod({
  userAgent,
  maxTouchPoints,
  hasPrompt,
  standalone,
}) {
  if (standalone) return null;
  if (hasPrompt) return "prompt";
  if (isInAppBrowser(userAgent)) return "in-app";
  if (isIOS(userAgent, maxTouchPoints)) return "ios";
  return "manual";
}

// --- o evento do navegador -------------------------------------------------

let deferredPrompt = null;
let installed = false;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

export function listenInstallPrompt(win = window) {
  win.addEventListener("beforeinstallprompt", (event) => {
    // sem isso o Chrome mostra a própria mini-barra logo na chegada
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  win.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installed = true;
    notify();
  });
}

export function subscribeInstallPrompt(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const hasInstallPrompt = () => deferredPrompt !== null;
export const wasInstalled = () => installed;

/** Abre o prompt nativo. Resolve com "accepted", "dismissed" ou null. */
export async function promptInstall() {
  const event = deferredPrompt;
  if (!event) return null;
  // o evento só pode ser usado uma vez
  deferredPrompt = null;
  notify();
  await event.prompt();
  const choice = await event.userChoice;
  return choice ? choice.outcome : null;
}
